import React, { JSX, useCallback, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  IconButton,
  Divider,
  Rating,
  Link,
  CardContent,
  Tooltip,
} from "@mui/material";
import axios from "../../api";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import HistoryModal from "layouts/AdminTickets/HistoryModal";
import FormatAssigneeHistory from "examples/ReuseFunctions/FormatAssigneeHistory";
import FormatStatusHistoryData from "examples/ReuseFunctions/FormatStatusHistory";
import { useSelector } from "react-redux";
import AddConversation from "examples/ReuseFunctions/AddConversation";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import FormatStatusTrackerData from "examples/ReuseFunctions/FormatStatusTracker";
import LocationDisplay from "components/LocationDisplay";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import SoftBox from "components/SoftBox";
import dayjs from "dayjs";
import { DownloadIcon } from "lucide-react";
import { generateInvoicePDF } from "layouts/stockManagers/orders/generateInvoicePdf";
import { RootState } from "store";
import { AxiosError } from "axios";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

const formatDate = (dateString: string | number | Date) => {
  const date = new Date(dateString);
  const optionsDate: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  const optionsTime: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const formattedDate = new Intl.DateTimeFormat("en-US", optionsDate).format(
    date
  );
  const formattedTime = new Intl.DateTimeFormat("en-US", optionsTime).format(
    date
  );
  return `${formattedDate}, ${formattedTime}`;
};

interface Media {
  multimedia_id: number;
  uploaded_by: string;
  media_stage: string | null;
  file_name: string;
  file_type: string;
  file_path: string;
  latitude: number;
  longitude: number;
}

interface Ratings {
  rating_id: number;
  rating: number | null;
  feedback: string | null;
}

interface Ticket {
  multimedia?: Media[] | null;
  title: string;
  status_tracker: string[];
  category_name: string;
  asset_name: string;
  customer_type: string;
  customer_name: string;
  division_name: string;
  customer_phone: string;
  customer_email: string;
  employee_name: string;
  status_name: string;
  address: string;
  city_name: string;
  state_name: string;
  address_type: string;
  description: string;
  rating: Ratings[];
  created_at: string | Date;
  feedback: string;
  customer_reject_reason: string;
  pending_reason: string;
  ticket_id: number;
  status_id: number;
  customer_comments: string;
}

// Custom cell renderers moved outside the component

interface ProductCellProps {
  value: string;
  tableMeta: any;
  rows: any[];
}

const ProductCell: React.FC<ProductCellProps> = ({
  value,
  tableMeta,
  rows,
}) => {
  const sparePart =
    tableMeta.rowData[0] === value
      ? rows[tableMeta.rowIndex]?.spare_part_name
      : "";
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold">
        {value}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {sparePart}
      </Typography>
    </Box>
  );
};

interface OrderDateCellProps {
  value: string;
}

const OrderDateCell: React.FC<OrderDateCellProps> = ({ value }) => {
  if (!value) return <>-</>;
  return <>{dayjs(value).format("MMM DD, YYYY")}</>;
};

interface OrderStatusCellProps {
  value: string;
}

const OrderStatusCell: React.FC<OrderStatusCellProps> = ({ value }) => {
  const getStatusColor = (status: string): string => {
    switch (status) {
      case "Delivered":
        return "#e1f2f2";
      case "Shipped":
        return "#1976d2";
      case "Pending":
        return "#fdf5e1";
      case "Cancelled":
        return "#fceddd";
      default:
        return "#9e9e9e";
    }
  };

  const bgColor = getStatusColor(value);

  return (
    <Box
      sx={{
        backgroundColor: bgColor,
        color: "#000",
        padding: "4px 8px",
        borderRadius: "8px",
        textAlign: "center",
        display: "inline-block",
        minWidth: "80px",
      }}
    >
      {value}
    </Box>
  );
};

interface TotalCellProps {
  value: string | number;
}

const TotalCell: React.FC<TotalCellProps> = ({ value }) => {
  return <span style={{ fontWeight: "bold" }}>{value}</span>;
};

interface ActionsCellProps {
  tableMeta: any;
  rows: any[];
  onDownload: (orderId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  tableMeta,
  rows,
  onDownload,
}) => {
  const orderId = rows[tableMeta.rowIndex]?.order_id;
  return (
    <Tooltip title="Download Invoice">
      <IconButton
        color="primary"
        onClick={() => onDownload(orderId)}
        size="small"
      >
        <DownloadIcon />
      </IconButton>
    </Tooltip>
  );
};

export interface ViewTicketProps {
  /** When true, render without layout and with Close instead of Back */
  embedded?: boolean;
  /** When in overlay, pass ticket id instead of using route params */
  ticketId?: number;
  /** When embedded, called when user closes (replaces back navigation) */
  onClose?: () => void;
}

const ViewTicket = ({ embedded, ticketId, onClose }: ViewTicketProps = {}) => {
  const { id: idFromParams } = useParams();
  const id = ticketId != null ? String(ticketId) : idFromParams;
  const user = useSelector((state: RootState) => state.auth.user);
  const { fetchError } = useSnackbar();
  const [ticketData, setTicketData] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [assigneeModalOpen, setAssigneeModalOpen] = useState(false);
  const [assigneeHistory, setAssigneeHistory] = useState<JSX.Element[] | null>(
    null
  );
  const [statusHistory, setStatusHistory] = useState<JSX.Element[] | null>(
    null
  );
  const [rows, setRows] = React.useState<
    { spare_part_name: string; [key: string]: any }[]
  >([]);
  const fetchedIdRef = React.useRef<string | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    if (!id || fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;
    setLoading(true);
    try {
      const ticketResponse = await axios.get(`/tickets/${id}`);
      const { data } = ticketResponse;
      const ticketData = data?.list;

      if (ticketData?.assignee_history) {
        setAssigneeHistory(FormatAssigneeHistory(ticketData?.assignee_history));
      }
      if (ticketData?.status_history) {
        setStatusHistory(FormatStatusHistoryData(ticketData?.status_history));
      }

      setTicketData(ticketData);
    } catch (error) {
      if (embedded && onClose) {
        onClose();
      } else {
        navigate("/customer/tickets-list");
      }
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response.data.error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, fetchError, navigate, embedded, onClose]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchedIdRef.current = null;
    };
  }, [fetchData]);

  const handleMediaOpen = (fileName: string) => {
    const url = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${fileName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleStatusOpen = () => {
    setStatusModalOpen(true);
  };

  const handleStatusClose = () => {
    setStatusModalOpen(false);
  };

  const handleAssigneeOpen = () => {
    setAssigneeModalOpen(true);
  };

  const handleAssigneeClose = () => {
    setAssigneeModalOpen(false);
  };

  const handleDownloadInvoice = async (orderId: number) => {
    try {
      const res = await axios.get(`/spare-order/${orderId}`);
      const orderData = res?.data;
      generateInvoicePDF(orderData);
    } catch (err) {
      console.error("Error fetching order for invoice:", err);
      fetchError("Failed to download invoice");
    }
  };

  // Render functions that use the component instead of inline JSX
  const renderProductCell = (value: string, tableMeta: any) => {
    return <ProductCell value={value} tableMeta={tableMeta} rows={rows} />;
  };

  const renderOrderDateCell = (value: string) => {
    return <OrderDateCell value={value} />;
  };

  const renderOrderStatusCell = (value: string) => {
    return <OrderStatusCell value={value} />;
  };

  const renderTotalCell = (value: string | number) => {
    return <TotalCell value={value} />;
  };

  const renderActionsCell = (value: any, tableMeta: any) => {
    return (
      <ActionsCell
        tableMeta={tableMeta}
        rows={rows}
        onDownload={handleDownloadInvoice}
      />
    );
  };

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      {
        name: "asset_type_name",
        label: "Product",
        options: {
          customBodyRender: renderProductCell,
        },
      },
      { name: "order_number", label: "Order ID" },
      {
        name: "order_date",
        label: "Order Date",
        options: {
          customBodyRender: renderOrderDateCell,
        },
      },
      {
        name: "order_status",
        label: "Status",
        options: {
          customBodyRender: renderOrderStatusCell,
        },
      },
      {
        name: "total_price",
        label: "Total",
        options: {
          customBodyRender: renderTotalCell,
        },
      },
      {
        name: "order_id",
        label: "Actions",
        options: {
          customBodyRender: renderActionsCell,
        },
      },
    ],
    [rows]
  );

  const tableOptions: MUIDataTableOptions = {
    selectableRows: "none",
    selectableRowsHeader: false,
    elevation: 0,
    tableBodyHeight: "auto",
    responsive: "standard",
  };

  const fetchSpareOrders = React.useCallback(async () => {
    try {
      const response = await axios.get(`/spare-order/ticket/${id}`);
      setRows(response?.data?.orders || []);
    } catch (error) {
      console.error(error);
    }
  }, [id]);

  React.useEffect(() => {
    if (id) fetchSpareOrders();
  }, [fetchSpareOrders, id]);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      await axios.get(`/spare-order/${id}`);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading && !ticketData) {
    return <PageLoader />;
  }

  const rawMultimedia = ticketData?.multimedia || [];
  const dedupedMultimedia = rawMultimedia.filter(
    (m, i, arr) => arr.findIndex((x) => x.multimedia_id === m.multimedia_id) === i
  );
  const employeeMedia = dedupedMultimedia.filter((media) => media.media_stage !== null);
  const preUploads = employeeMedia.filter((m) => m.media_stage === "pre");
  const postUploads = employeeMedia.filter((m) => m.media_stage === "post");
  const customerMediaList = dedupedMultimedia.filter((m) => m.media_stage === null);
  const hasCustomerMedia = customerMediaList.length > 0;

  const cardContent = (
    <>
      <Card
        className="view-ticket"
        style={{
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"center"}
          className="view-ticket-title"
        >
          <IconButton
            className="back-btn"
            onClick={
              embedded && onClose
                ? onClose
                : () => navigate("/customer/tickets-list")
            }
          >
            {embedded && onClose ? (
              <CloseIcon />
            ) : (
              <ArrowBackIcon />
            )}
          </IconButton>
          <Typography variant="h5" gutterBottom>
            Ticket Details
          </Typography>
        </Grid>
        {ticketData?.status_tracker && (
          <FormatStatusTrackerData trackingData={ticketData.status_tracker} />
        )}

        <Grid container className="view-ticket-details">
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Title</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.title}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Asset</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.asset_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Name</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.customer_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Mobile</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.customer_phone}</span>
              </Box>
            </Typography>
          </Grid>

          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Email</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.customer_email}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Created At</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {ticketData?.created_at
                    ? formatDate(ticketData?.created_at)
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Assignee</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.employee_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Status</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {ticketData?.status_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Status History</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={() => handleStatusOpen()}
                >
                  Click here
                </Link>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Assignee History</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <Link
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAssigneeOpen()}
                >
                  Click here
                </Link>
              </Box>
            </Typography>
          </Grid>
          <Grid container spacing={0}>
            {/* Label */}
            <Grid item xs={12} sm={3} md={2} ml={-1} mr={1}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}>
                <span>Address</span>
              </Typography>
            </Grid>

            {/* Value */}
            <Grid item xs={12} sm={9} md={10} ml={-1}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  whiteSpace: "pre-wrap", // keep line breaks
                  wordBreak: "break-word", // wrap long words
                }}
              >
                <span>
                  :{" "}
                  {`${ticketData?.address}, ${ticketData?.city_name}, ${ticketData?.state_name}, ${ticketData?.address_type}`}
                </span>
              </Typography>
            </Grid>
          </Grid>
          {/* Description */}
          <Grid item xs={12} sm={12} md={12} sx={{ my: 1 }}>
            <Grid container>
              {/* Label */}
              <Grid item xs={4} sm={4} md={2}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="flex-start">
                    <span>Description</span>
                  </Box>
                </Typography>
              </Grid>
              {/* Value */}
              <Grid item xs={8} sm={8} md={10} ml={-1}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "normal",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  : {ticketData?.description}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {ticketData?.rating && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Rating</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>
                      :{" "}
                      <Rating
                        name="simple-controlled"
                        value={ticketData?.rating?.[0]?.rating || 0}
                        readOnly
                      />
                    </span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.feedback && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Feedback</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.feedback}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.customer_reject_reason && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Reject Reason</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.customer_reject_reason}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          {ticketData?.pending_reason && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Pending Reason</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {ticketData?.pending_reason}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
        {(ticketData?.status_id === 2 || ticketData?.status_id === 3) && (
          <>
            <Divider sx={{ height: 2, backgroundColor: "blue" }} />
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Employee
              </Typography>
            </Grid>
            <div className="comments-conv">
              {ticketData && user && (
                <AddConversation
                  user={{
                    ...user,
                    Role: Array.isArray(user.Role) ? user.Role : [user.Role],
                  }}
                  data={ticketData}
                />
              )}
            </div>
          </>
        )}
        <Divider sx={{ my: 2, height: 2, backgroundColor: "blue" }} />
        <div className="view-ticket-details">
          <Typography variant="h5" gutterBottom>
            Customer Media
          </Typography>
          {hasCustomerMedia ? (
            <div className="upload-media ticket-media">
              {customerMediaList.map((media) => (
                  <div
                    className="media-container view-media"
                    key={media.multimedia_id}
                  >
                    <button
                      type="button"
                      onClick={() => handleMediaOpen(media?.file_name)}
                      style={{
                        all: "unset", // removes default button styles
                        cursor: "pointer",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {media.file_type === "Photo" ? (
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`media-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 140,
                            objectFit: "cover",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      )}
                    </button>
                  </div>
                ))}
            </div>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <span>No Media</span>
            </Typography>
          )}
        </div>
        {/* Employee Media View */}
        <Divider sx={{ my: 2, height: 2, backgroundColor: "blue" }} />
        <div className="view-ticket-details">
          <Box sx={{ my: 2 }}>
            <Typography variant="h4">
              <strong>Employee Uploaded Media</strong>
            </Typography>

            <Typography variant="h6" sx={{ pt: 3, pb: 3 }}>
              <strong> PRE UPLOADS</strong>
            </Typography>
            <Grid container spacing={2}>
              {preUploads.map((media) => (
                <Grid item key={media.multimedia_id} xs={12} sm={6} md={3}>
                  <Card
                    sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}
                  >
                    {media.file_type === "Photo" ? (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset", // remove default button styles
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`pre-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset",
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      </button>
                    )}

                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <LocationOnIcon fontSize="small" color="primary" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        <LocationDisplay
                          latitude={media.latitude}
                          longitude={media.longitude}
                        />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ my: 2, height: 3, backgroundColor: "blue" }} />
            <Typography variant="h6" sx={{ pt: 5, pb: 3 }}>
              <strong> POST UPLOADS</strong>
            </Typography>
            <Grid container spacing={2}>
              {postUploads.map((media) => (
                <Grid item key={media.multimedia_id} xs={12} sm={6} md={3}>
                  <Card
                    sx={{ borderRadius: 2, boxShadow: 3, overflow: "hidden" }}
                  >
                    {media.file_type === "Photo" ? (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset", // remove default button styles
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <img
                          src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                          alt={`post-${media.multimedia_id}`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                          }}
                        />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMediaOpen(media.file_name)}
                        style={{
                          all: "unset", // remove default button styles
                          cursor: "pointer",
                          display: "block",
                          width: "100%",
                        }}
                      >
                        <video
                          controls
                          style={{
                            width: "100%",
                            height: 200,
                            objectFit: "cover",
                            cursor: "pointer",
                          }}
                          onClick={() => handleMediaOpen(media.file_name)}
                        >
                          <source
                            src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${media.file_name}`}
                            type="video/mp4"
                          />
                          <track
                            kind="captions"
                            src=""
                            label="No captions available"
                            default
                          />
                        </video>
                      </button>
                    )}
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <LocationOnIcon fontSize="small" color="primary" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 500 }}
                      >
                        <LocationDisplay
                          latitude={media.latitude}
                          longitude={media.longitude}
                        />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </div>

        {assigneeModalOpen && (
          <HistoryModal
            title={"Assignee"}
            data={assigneeHistory}
            isOpen={assigneeModalOpen}
            onClose={handleAssigneeClose}
          />
        )}
        {statusModalOpen && (
          <HistoryModal
            title={"Status"}
            data={statusHistory}
            isOpen={statusModalOpen}
            onClose={handleStatusClose}
          />
        )}
      </Card>
      <Card sx={{ p: 1, m: "16px auto", maxWidth: "20000px" }}>
        <SoftBox>
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Related Orders"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
        </SoftBox>
      </Card>
    </>
  );

  if (embedded) {
    return <Box sx={{ p: 2 }}>{cardContent}</Box>;
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {cardContent}
    </DashboardLayout>
  );
};

export default ViewTicket;
